import * as yup from 'yup';
import { currencyCodes } from '~/globals';
import {
  isCountryRequiredItemType,
  isCruiseItemType,
  isFlexibleCancellationEndDateRequired,
  isFlightEndDateRequired,
  isReasonOtherTextRequired,
  isTourOrCruiseItemType,
  isVendorDepositPaymentDateRequired,
  isVendorPaymentDateRequired,
} from '~/validation/schemas/customOfferValidationPredicates';

import { calculateCruisePostPurchaseTotals } from '~/components/Common/CustomOffers/calculations';
import {
  CRUISE_POST_PURCHASE_TYPE_REFARE,
  CRUISE_POST_PURCHASE_TYPE_VALUES,
  CUSTOM_OFFER_CANCELLATION_POLICY_TYPES,
  FLIGHT_IATA_AIRLINE_INVALID_MESSAGE,
  FLIGHT_IATA_AIRLINE_REGEX,
  FLIGHT_IATA_AIRPORT_INVALID_MESSAGE,
  FLIGHT_IATA_AIRPORT_REGEX,
  FLIGHT_PNR_INVALID_MESSAGE,
  FLIGHT_PNR_REGEX,
  FLIGHT_PROVIDER_OPTIONS,
  FLIGHT_TRAVEL_SCOPE_VALUES,
  FLIGHT_TYPE_MULTI_CITY,
  FLIGHT_TYPE_ONE_WAY,
  FLIGHT_TYPE_RETURN,
  type FlightType,
  LE_ENTITIES,
  POST_PURCHASE_TYPE_NAME_CORRECTION,
  PURCHASE_TYPE_POST_PURCHASE,
  PURCHASE_TYPE_SALES,
} from '~/components/Common/CustomOffers/constants';

import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';

const today = new Date();
today.setHours(0, 0, 0, 0);
const flightProviderValues = FLIGHT_PROVIDER_OPTIONS.map(({ value }) => value);

const itemSchema = {
  type: yup
    .string()
    .required('Please select an item type.')
    .oneOf(
      ['flight', 'cruise', 'tour', 'hotel', 'experience', 'ics_store', 'le_live', 'other', 'custom-offer-package'],
      'Please select a valid item type.',
    ),
  name: yup
    .string()
    .required('Please enter the item name.')
    .max(300, 'Item name cannot be longer than 300 characters.'),
  description: yup.string().required('Please enter the item description.'),
  service_information: yup.string().required('Please enter the terms and conditions.'),
  vendor: yup.string().required('Please enter the vendor name.'),
  vendor_reference_number: yup.string().optional(),
  salesforce_case_number: yup.string().optional(),
  start_date: yup
    .date()
    .required('Please enter the start date.')
    .test('is-before-or-equal-to-end', 'Start date must be on or before the end date.', function (value) {
      const endDate = this.parent.end_date;
      if (!value || !endDate) {
        return true;
      }
      const start = new Date(value);
      const end = new Date(endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      return start.getTime() <= end.getTime();
    }),
  end_date: yup
    .date()
    .required('Please enter the end date.')
    .test('is-after-or-equal-to-start', 'End date must be on or after the start date.', function (value) {
      const startDate = this.parent.start_date;
      if (!value || !startDate) {
        return true;
      }
      const start = new Date(startDate);
      const end = new Date(value);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      return end.getTime() >= start.getTime();
    }),

  country: yup.string().when('type', {
    is: (type: string | undefined) => isCountryRequiredItemType(type),
    then: (schema) => schema.required('Please enter the country.'),
    otherwise: (schema) => schema.optional(),
  }),
  city: yup.string().when('type', {
    is: (type: string | undefined) => isTourOrCruiseItemType(type),
    then: (schema) => schema.required('Please enter the departure city.'),
    otherwise: (schema) => schema.optional(),
  }),

  // cruise specific fields
  // required if type is cruise, otherwise optional
  cruise_line: yup.string().when('type', {
    is: (type: string | undefined) => isCruiseItemType(type),
    then: (schema) => schema.required('Please enter the cruise line.'),
    otherwise: (schema) => schema.optional(),
  }),
  ship_name: yup.string().when('type', {
    is: (type: string | undefined) => isCruiseItemType(type),
    then: (schema) => schema.required('Please enter the ship name.'),
    otherwise: (schema) => schema.optional(),
  }),
  ship_cabin: yup.string().when('type', {
    is: (type: string | undefined) => isCruiseItemType(type),
    then: (schema) => schema.required('Please enter the ship cabin.'),
    otherwise: (schema) => schema.optional(),
  }),
  ship_rate: yup.string().when('type', {
    is: (type: string | undefined) => isCruiseItemType(type),
    then: (schema) => schema.required('Please enter the ship rate.'),
    otherwise: (schema) => schema.optional(),
  }),
};

const travellerSchema = yup.object({
  name: yup.string().when('first_name', {
    is: (firstName: string | undefined) => !firstName,
    then: (schema) => schema.required('Please enter the traveller name.'),
  }),
  first_name: yup.string(),
  middle_name: yup.string(),
  last_name: yup.string(),
  type: yup
    .string()
    .required('Please select a traveller type.')
    .oneOf(['adult', 'child', 'infant'], 'Please select a valid traveller type.'),
  country: yup.string().required('Please select the traveller country.'),
  date_of_birth: yup.date().optional().nullable().max(new Date(), 'Date of birth must be in the past.'),
  employee: yup.object().optional().nullable(),
});

const leLiveItemSchema = yup.object(itemSchema).shape({
  description: yup.string().required('Please enter the item description.'),
  service_information: yup.string().required('Please enter the terms and conditions.'),
  vendor_reference_number: yup.string().trim().required('Please enter the supplier booking reference.'),
});

const leLiveTravellerSchema = travellerSchema.shape({
  country: yup.string().optional().nullable(),
});

const createItemFlightBaseSchema = (purchaseType: 'sales' | 'post-purchase' = 'sales') => ({
  type: yup.string().required('Please select an item type.').oneOf(['flight'], 'Please select a valid item type.'),
  provider: yup
    .string()
    .required('Please select the provider.')
    .oneOf(flightProviderValues, 'Please select a valid provider.'),
  vendor_reference_number: yup
    .string()
    .trim()
    .required('Please enter the PNR.')
    .matches(FLIGHT_PNR_REGEX, FLIGHT_PNR_INVALID_MESSAGE),
  name: yup
    .string()
    .required('Please enter the item name.')
    .max(300, 'Item name cannot be longer than 300 characters.'),
  service_information:
    purchaseType === 'sales'
      ? yup.string().required('Please enter the terms and conditions.')
      : yup.string().optional().nullable(),
  // This is now Zendesk Ticket Number
  salesforce_case_number: yup.string().required('Please enter the Zendesk ticket number.'),
  // This is the departure date for flights
  start_date: yup.date().when('flight_type', {
    is: 'one-way',
    then: (schema) =>
      schema
        .required('Please enter the start date.')
        .test('is-in-the-future', 'Start date must be in the future.', function (value) {
          const currentDate = new Date();
          currentDate.setHours(0, 0, 0, 0);
          const start = new Date(value);
          start.setHours(0, 0, 0, 0);
          return start.getTime() >= currentDate.getTime();
        }),
    otherwise: (schema) =>
      schema
        .required('Please enter the start date.')
        .test('is-before-or-equal-to-end', 'Start date must be on or before the end date.', function (value) {
          const endDate = this.parent.end_date;
          if (!value || !endDate) {
            return true;
          }
          const start = new Date(value);
          const end = new Date(endDate);
          start.setHours(0, 0, 0, 0);
          end.setHours(0, 0, 0, 0);
          return start.getTime() <= end.getTime();
        }),
  }),
  // This is the return date for flights
  end_date: yup.date().when('flight_type', {
    is: (val: string | undefined) => isFlightEndDateRequired(val),
    then: (schema) =>
      schema
        .required('Please enter the end date.')
        .test('is-after-or-equal-to-start', 'End date must be on or after the start date.', function (value) {
          const startDate = this.parent.start_date;
          if (!value || !startDate) {
            return true;
          }
          const start = new Date(startDate);
          const end = new Date(value);
          start.setHours(0, 0, 0, 0);
          end.setHours(0, 0, 0, 0);
          return end.getTime() >= start.getTime();
        }),
    otherwise: (schema) => schema.optional(),
  }),
});

const toUpperCaseCode = (value: unknown) => (typeof value === 'string' ? value.trim().toUpperCase() : value);

// The calendar invite resolves airport timezones by IATA code, so "syd" is stored as "SYD".
const airportSchema = (requiredMessage: string, isBusinessTraveller: boolean) => {
  const base = yup.string().required(requiredMessage);

  return isBusinessTraveller
    ? base.transform(toUpperCaseCode).matches(FLIGHT_IATA_AIRPORT_REGEX, FLIGHT_IATA_AIRPORT_INVALID_MESSAGE)
    : base;
};

// Flight leg segment schema - shared between create and edit modes
const createFlightLegSegmentSchema = (isBusinessTraveller: boolean) =>
  yup.object({
    // The calendar event describes each flight as "airline - number"; on LEBT the import stores the code here.
    airline: isBusinessTraveller
      ? yup
          .string()
          .transform(toUpperCaseCode)
          .required('Please enter the airline.')
          .matches(FLIGHT_IATA_AIRLINE_REGEX, FLIGHT_IATA_AIRLINE_INVALID_MESSAGE)
      : yup.string().optional(),
    departureAirport: airportSchema('Please enter the departure airport.', isBusinessTraveller),
    arrivalAirport: airportSchema('Please enter the arrival airport.', isBusinessTraveller),
    departureDate: yup.string().required('Please enter the departure date.'),
    arrivalDate: yup.string().required('Please enter the arrival date.'),
    bookingClass: yup
      .string()
      .optional()
      .transform((value) => (typeof value === 'string' ? value.trim() : value))
      .matches(/^[A-Za-z]?$/, 'Booking class must be a single letter.'),
  });

const createFlightLegSchema = (isBusinessTraveller: boolean) =>
  yup.object({
    segments: yup
      .array()
      .of(createFlightLegSegmentSchema(isBusinessTraveller))
      .min(1, 'Each leg must have at least one segment.')
      .required('Segments are required for each leg.'),
  });

type LegCountRule = { min: number; max?: number; message: string };

// The calendar invite mirrors the itinerary, so the leg count has to match the journey shape.
const LEG_COUNT_BY_FLIGHT_TYPE: Record<FlightType, LegCountRule> = {
  [FLIGHT_TYPE_ONE_WAY]: { min: 1, max: 1, message: 'A one-way flight has exactly one leg.' },
  [FLIGHT_TYPE_RETURN]: { min: 2, max: 2, message: 'A return flight has exactly two legs: outbound and return.' },
  [FLIGHT_TYPE_MULTI_CITY]: { min: 2, message: 'A multi-city flight needs at least two legs.' },
};

const isFlightType = (flightType: unknown): flightType is FlightType =>
  typeof flightType === 'string' && Object.prototype.hasOwnProperty.call(LEG_COUNT_BY_FLIGHT_TYPE, flightType);

export const getLegCountRule = (flightType: unknown): LegCountRule | undefined =>
  isFlightType(flightType) ? LEG_COUNT_BY_FLIGHT_TYPE[flightType] : undefined;

// Legs are optional for other tenants. LEBT requires them, sized to the item's flight type, with IATA codes.
export const createFlightLegsSchema = (tenant?: string) => {
  const isBusinessTraveller = tenant === LE_BUSINESS_TRAVELLER;
  const legsSchema = yup.array().of(createFlightLegSchema(isBusinessTraveller));

  if (!isBusinessTraveller) {
    return yup.lazy(() => legsSchema.optional());
  }

  const requiredLegs = legsSchema.min(1, 'At least one flight leg is required.').required('Flight legs are required.');

  return yup.lazy((_legs, options) => {
    const expected = getLegCountRule(options.parent?.flight_type);

    return expected
      ? requiredLegs.test(
          'leg-count-matches-flight-type',
          expected.message,
          (legs) => !legs?.length || (legs.length >= expected.min && legs.length <= (expected.max ?? Infinity)),
        )
      : requiredLegs;
  });
};

const TRAVEL_SCOPE_REQUIRED_MESSAGE = 'Please select whether this is a domestic or international flight.';

const createTravelScopeSchema = (purchaseType: 'sales' | 'post-purchase' = 'sales') =>
  purchaseType === PURCHASE_TYPE_SALES
    ? yup
        .string()
        .oneOf([...FLIGHT_TRAVEL_SCOPE_VALUES], TRAVEL_SCOPE_REQUIRED_MESSAGE)
        .required(TRAVEL_SCOPE_REQUIRED_MESSAGE)
    : yup
        .string()
        .oneOf([...FLIGHT_TRAVEL_SCOPE_VALUES])
        .optional()
        .nullable();

const createItemFlightSchema = (tenant?: string, purchaseType: 'sales' | 'post-purchase' = 'sales') => ({
  ...createItemFlightBaseSchema(purchaseType),
  travel_scope: createTravelScopeSchema(purchaseType),
  flight_legs: createFlightLegsSchema(tenant),
});

const travellerFlightSchema = yup.object({
  first_name: yup.string().required('Please enter the traveller name.'),
  middle_name: yup.string().optional().nullable(),
  last_name: yup.string().required('Please enter the traveller last name.'),
  type: yup
    .string()
    .required('Please select a traveller type.')
    .oneOf(['adult', 'child', 'infant'], 'Please select a valid traveller type.'),
  date_of_birth: yup.date().optional().nullable().max(new Date(), 'Date of birth must be in the past.'),
  fare_and_taxes: yup
    .number()
    .typeError('Please enter a valid number for fare and taxes.')
    .default(0)
    .required('Please enter fare and taxes.')
    .moreThan(0, 'Fare and taxes must be greater than 0.')
    .max(9999999999, 'Fare and taxes cannot be larger than 9,999,999,999.'),
});

const travellerFlightPostPurchaseSchema = yup.object({
  first_name: yup.string().required('Please enter the traveller name.'),
  middle_name: yup.string().optional().nullable(),
  last_name: yup.string().required('Please enter the traveller last name.'),
  type: yup
    .string()
    .required('Please select a traveller type.')
    .oneOf(['adult', 'child', 'infant'], 'Please select a valid traveller type.'),
  date_of_birth: yup.date().optional().nullable().max(new Date(), 'Date of birth must be in the past.'),
  airline_fee: yup
    .number()
    .typeError('Please enter a valid number for airline fee.')
    .default(0)
    .required('Please enter the airline fee.')
    .min(0, 'Airline fee must be 0 or greater.')
    .max(9999999999, 'Airline fee cannot be larger than 9,999,999,999.'),
  le_fee: yup
    .number()
    .typeError('Please enter a valid number for LE fee.')
    .default(0)
    .required('Please enter the LE fee.')
    .min(0, 'LE fee must be 0 or greater.')
    .max(9999999999, 'LE fee cannot be larger than 9,999,999,999.'),
});

const internalDiscountTrackingSchema = yup
  .string()
  .optional()
  .nullable()
  .transform((v) => (v == null || String(v).trim() === '' ? null : String(v).trim()))
  .test('integer', 'Enter a whole number only (optional).', (v) => v == null || /^-?\d+$/.test(v))
  .max(15, 'Number cannot exceed 15 digits.');

// LEBT service fee selection. Optional. If present, every required field must be set.
const lebtServiceFeeSchema = yup
  .object({
    source: yup.string().oneOf(['CALL_CENTRE', 'AFTER_HOURS_CALL_CENTRE']).required(),
    charge_type: yup.string().oneOf(['BOOKING', 'BOOKING_CHANGES', 'TICKET_REFUND']).required(),
    destination: yup.string().oneOf(['DOMESTIC', 'INTERNATIONAL']).optional(),
    product: yup.string().oneOf(['HOTELS', 'SINGLE_RETURN_FLIGHTS', 'MULTI_CITY_FLIGHTS']).optional(),
    concierge_mode: yup.boolean().required(),
    amount: yup
      .number()
      .typeError('Please enter a valid number for service fee.')
      // svc-order stores the amount in a numeric(12,2) column.
      .min(-9999999999.99, 'Service fee cannot be smaller than -9,999,999,999.99.')
      .max(9999999999.99, 'Service fee cannot be larger than 9,999,999,999.99.')
      .test(
        'two-decimals',
        'Service fee can have at most 2 decimal places.',
        (value) => value == null || Math.abs(value * 100 - Math.round(value * 100)) < 1e-6,
      )
      .required('Please enter the service fee amount.'),
  })
  .optional()
  .default(undefined);

// On the LEBT brand the pre-defined service fee must be selected; other brands keep it optional.
// Brand is supplied through the form's validation context ($brand).
const lebtServiceFeeBrandAwareSchema = lebtServiceFeeSchema.when('$brand', {
  is: LE_BUSINESS_TRAVELLER,
  then: (schema) => schema.required('Please select a service fee or enter a custom amount.'),
});

const customOfferPaymentFlowV2Schema = yup.object({
  type: yup
    .string()
    .oneOf(['full_payment', 'payment_schedule', 'manual'], 'Please select a valid payment flow.')
    .optional()
    .nullable(),
  deposit_amount: yup
    .number()
    .typeError('Please enter a valid number for deposit amount.')
    .default(0)
    .when('type', {
      is: 'payment_schedule',
      then: (schema) => schema.moreThan(0, 'Deposit amount must be greater than 0.'),
      otherwise: (schema) => schema,
    }),
  balance_amount: yup.number().default(0),
  balance_due_date: yup
    .date()
    .label('Balance due date')
    .when('type', {
      is: 'payment_schedule',
      then: () =>
        yup.date().required('Please enter the balance due date.').min(today, 'Balance due date must be in the future.'),
      otherwise: () => yup.date().nullable(),
    }),
  payment_deadline: yup
    .date()
    .label('Payment deadline')
    .when('type', {
      is: (type: string) => type === 'full_payment' || type === 'payment_schedule',
      then: () =>
        yup.date().required('Please enter the payment deadline.').min(today, 'Payment deadline must be in the future.'),
      otherwise: () => yup.date().nullable(),
    }),
  manual_deposit_amount: yup
    .number()
    .typeError('Please enter a valid number for manual deposit amount.')
    .default(0)
    .when('type', {
      is: 'manual',
      then: (schema) => schema.moreThan(0, 'Manual deposit amount must be greater than 0.'),
      otherwise: (schema) => schema,
    }),
  manual_deposit_due_date: yup
    .date()
    .label('Manual deposit due date')
    .when('type', {
      is: 'manual',
      then: () =>
        yup
          .date()
          .required('Please enter the manual deposit due date.')
          .min(today, 'Manual deposit due date must be in the future.'),
      otherwise: () => yup.date().nullable(),
    }),
  manual_deposit_payment_made: yup.boolean().default(false),
  manual_deposit_pi_number: yup.string().when(['type', 'manual_deposit_payment_made'], {
    is: (type: string, manualDepositPaymentMade: boolean) => type === 'manual' && manualDepositPaymentMade,
    then: (schema) => schema.required('Please enter the manual deposit PI number.'),
    otherwise: (schema) => schema.optional(),
  }),
  manual_balance_amount: yup
    .number()
    .typeError('Please enter a valid number for manual balance amount.')
    .default(0)
    .when('type', {
      is: 'manual',
      then: (schema) => schema.moreThan(0, 'Manual balance amount must be greater than 0.'),
      otherwise: (schema) => schema,
    }),
  manual_balance_due_date: yup
    .date()
    .label('Manual balance due date')
    .when('type', {
      is: 'manual',
      then: () =>
        yup
          .date()
          .required('Please enter the manual balance due date.')
          .min(today, 'Manual balance due date must be in the future.'),
      otherwise: () => yup.date().nullable(),
    }),
});

const refareNewFareSchema = yup
  .number()
  .typeError('Please enter a valid new fare.')
  .min(0, 'New fare must be 0 or greater.');
const refareNewTaxAndSurchargesSchema = yup
  .number()
  .typeError('Please enter valid new tax and surcharges.')
  .min(0, 'New tax and surcharges must be 0 or greater.');
const refareDepositFeeAlreadyPaidSchema = yup
  .number()
  .typeError('Please enter a valid deposit or fee already paid.')
  .min(0, 'Deposit or fee already paid must be 0 or greater.');

const paymentMetadataSchema = yup.object({
  vendor_payment_type: yup
    .string()
    .required('Please select the vendor payment type.')
    .oneOf(['deposit', 'full_payment'], 'Please select a valid vendor payment type.'),
  vendor_due_date: yup
    .date()
    .required('Please enter the vendor due date.')
    .min(today, 'Vendor due date must be in the future.'),
  vendor_deposit_1_amount: yup
    .number()
    .typeError('Please enter a valid number for deposit amount.')
    .label('Deposit 1 amount')
    .default(0)
    .optional()
    .min(0),
  vendor_deposit_1_due_date: yup
    .date()
    .when('vendor_payment_type', ([vendor_payment_type], schema) =>
      vendor_payment_type === 'deposit'
        ? schema.min(today, 'Vendor deposit due date must be in the future.')
        : schema.optional(),
    ),
  vendor_deposit_payment_made: yup.boolean().label('Deposit paid?').default(false).required(),
  customer_deposit_payment_made: yup.boolean().label('Deposit paid?').default(false).notRequired(),
  vendor_was_paid: yup.boolean().label('Vendor payment made?').default(false).required(),
  deposit_pi_number_customer: yup.string().label('Deposit PI number').optional(),
  deposit_vcc_digits: yup.string().label('Deposit VCC digits').optional(),
  final_payment_vcc_digits: yup.string().label('Final payment VCC digits').optional(),
  margin: yup.number().typeError('Please enter a valid number for margin.').default(0),
  le_service_fee: yup.number().typeError('Please enter a valid number for LE service fee.').default(0),
  cruiseline_fee: yup
    .number()
    .typeError('Please enter a valid cruiseline fee.')
    .optional()
    .default(0)
    .min(0, 'Cruiseline fee must be 0 or greater.'),
  cruiseline_additional_charges: yup
    .number()
    .typeError('Please enter valid cruiseline additional charges.')
    .optional()
    .default(0)
    .min(0, 'Cruiseline additional charges must be 0 or greater.'),
  le_fee: yup
    .number()
    .typeError('Please enter a valid LE fee.')
    .optional()
    .default(0)
    .min(0, 'LE fee must be 0 or greater.'),
  refare_new_fare: refareNewFareSchema.optional(),
  refare_new_tax_and_surcharges: refareNewTaxAndSurchargesSchema.optional(),
  refare_deposit_fee_already_paid: refareDepositFeeAlreadyPaidSchema.optional(),
  lebt_service_fee: lebtServiceFeeBrandAwareSchema,
  final_amount_vendor: yup
    .number()
    .typeError('Please enter a valid number for final amount.')
    .required('Please enter the final amount for the vendor.')
    .min(1, 'Final amount for the vendor must be at least 1.'),
  final_amount_customer: yup
    .number()
    .typeError('Please enter a valid number for final amount.')
    .when('payment_flow_v2.type', {
      is: (paymentFlowType: string | null | undefined) => !paymentFlowType,
      then: (schema) =>
        schema
          .required('Please enter the final amount for the customer.')
          .min(1, 'Final amount for the customer must be at least 1.'),
      otherwise: (schema) => schema.optional(),
    }),
  payment_type: yup.string().when('payment_flow_v2.type', {
    is: (paymentFlowType: string | null | undefined) => !paymentFlowType,
    then: (schema) =>
      schema
        .required('Please select the customer payment type.')
        .oneOf(['deposit', 'full_payment'], 'Please select a valid customer payment type.'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  customer_deposit_1_due_date: yup
    .date()
    .label('Deposit 1 due date')
    .when(['payment_flow_v2.type', 'payment_type'], ([paymentFlowType, payment_type], schema) =>
      !paymentFlowType && payment_type === 'deposit'
        ? schema.min(today, 'Customer deposit due date must be in the future.')
        : schema.optional().nullable(),
    ),
  customer_deposit_1_amount: yup
    .number()
    .typeError('Please enter a valid number for deposit amount.')
    .label('Deposit 1 amount')
    .default(0)
    .when(['payment_flow_v2.type', 'payment_type'], ([paymentFlowType, payment_type], schema) =>
      !paymentFlowType && payment_type === 'deposit'
        ? schema.moreThan(0, 'Customer deposit amount must be greater than 0.')
        : schema.optional(),
    ),
  payment_flow_v2: customOfferPaymentFlowV2Schema.optional().default(undefined),
  vendor_deposit_payment_date: yup
    .date()
    .label('Vendor Deposit Payment Date')
    .when('vendor_deposit_payment_made', {
      is: (vendorDepositPaymentMade: boolean | undefined) =>
        isVendorDepositPaymentDateRequired(vendorDepositPaymentMade),
      then: () =>
        yup.date().required('This field is required when Vendor deposit was made').typeError('Date cannot be blank'),
      otherwise: () => yup.date().nullable(),
    }),
  vendor_payment_date: yup
    .date()
    .label('Vendor Payment Date')
    .when('vendor_was_paid', {
      is: (vendorWasPaid: boolean | undefined) => isVendorPaymentDateRequired(vendorWasPaid),
      then: () =>
        yup.date().required('This field is required when Vendor payment was made').typeError('Date cannot be blank'),
      otherwise: () => yup.date().nullable(),
    }),
  refund_received: yup.date().nullable().notRequired(),
  le_entity: yup.string().oneOf(LE_ENTITIES).required('Please select the LE Entity.'),
  commission_amount: yup.number().optional().typeError('Commission amount cannot be empty. Enter 0 if not applicable.'),
  commission_received: yup.boolean().optional().nullable(),
  additional_vendor_payment: yup
    .number()
    .optional()
    .typeError('Additional vendor payment cannot be empty. Enter 0 if not applicable.'),
  created_by: yup.string().optional(),
  sales_agent_name: yup.string().required('Please select the Wholesale Agent.'),
  cancellation_type: yup.string().oneOf(CUSTOM_OFFER_CANCELLATION_POLICY_TYPES).optional().nullable(),
  cancellation_end_date: yup.date().when('cancellation_type', {
    is: (cancellationType: string | null | undefined) => isFlexibleCancellationEndDateRequired(cancellationType),
    then: () =>
      yup
        .date()
        .required('Please enter the cancellation end date for flexible policy.')
        .typeError('Cancellation end date cannot be blank.'),
    otherwise: () => yup.date().nullable(),
  }),
  sales_attribution: yup.string().required('Sales Attribution is required'),
  // Note: unlike other fields, this should be nullable and not default to 0 if cleared or not set
  // https://github.com/jquense/yup/issues/500#issuecomment-569343680
  additional_margin: yup
    .number()
    .transform((value, original) => (original === '' || isNaN(original) ? null : value))
    .nullable(),
  reason_additional_info: yup.string().optional().nullable(),
  reason_issue_raised: yup.boolean().optional().nullable(),
  internal_discount_tracking: internalDiscountTrackingSchema,
  // New flag to indicate vendor payment is non-refundable (manual confirmation flow)
  vendor_is_non_refundable: yup.boolean().optional().default(false),
  price_includes_gst: yup.boolean().default(false),
  gst_amount: yup.number().default(0),
  final_amount_customer_display: yup
    .number()
    .typeError('Please enter a valid number for final amount to display.')
    .optional()
    .nullable(),
});

const paymentMetadataFlightSchema = yup.object({
  margin: yup.number().typeError('Please enter a valid number for margin.').default(0),
  le_service_fee: yup.number().typeError('Please enter a valid number for service fee.').default(0),
  service_fee_type: yup.string().default('No fee'),
  lebt_service_fee: lebtServiceFeeBrandAwareSchema,
  payment_type: yup.string().oneOf(['full_payment']).default('full_payment'),
  le_entity: yup.string().oneOf(LE_ENTITIES).required('Please select the LE Entity.'),
  created_by: yup.string().optional(),
  sales_agent_name: yup.string(),
  sales_attribution: yup.string().required('Sales Attribution is required'),
  block_customer_payment_notifications: yup.boolean().default(false),
  payment_flow_v2: customOfferPaymentFlowV2Schema
    .shape({
      type: yup
        .string()
        .oneOf(['full_payment', 'manual'], 'Please select a valid payment flow.')
        .required('Please select a payment flow.'),
    })
    .required(),
});

const appendCustomOfferFlightPaymentTests = <T extends yup.AnyObjectSchema>(schema: T) =>
  schema
    .test('v2-flight-manual-requires-package-link', function (value) {
      if (value?.payment_metadata?.payment_flow_v2?.type !== 'manual') {
        return true;
      }

      if (value.fk_package_custom_offer_id && value.hiddenFromCustomer) {
        return true;
      }

      return this.createError({
        path: 'payment_metadata.payment_flow_v2.type',
        message:
          'Deposit payments are only available for flights hidden from the customer portal and linked to a package offer.',
      });
    })
    .test('v2-manual-deposit-amount', function (value) {
      if (value?.payment_metadata?.payment_flow_v2?.type !== 'manual') {
        return true;
      }

      const depositAmount = Number(value.payment_metadata.payment_flow_v2.manual_deposit_amount ?? 0);
      const price = Number(value.price ?? 0);

      if (depositAmount <= price) {
        return true;
      }

      return this.createError({
        path: 'payment_metadata.payment_flow_v2.manual_deposit_amount',
        message: 'Manual deposit amount cannot exceed the sell price.',
      });
    })
    .test('v2-manual-balance-amount', function (value) {
      if (value?.payment_metadata?.payment_flow_v2?.type !== 'manual') {
        return true;
      }

      const depositAmount = Number(value.payment_metadata.payment_flow_v2.manual_deposit_amount ?? 0);
      const balanceAmount = Number(value.payment_metadata.payment_flow_v2.manual_balance_amount ?? 0);
      const price = Number(value.price ?? 0);
      const expectedBalanceAmount = Math.round((price - depositAmount) * 100) / 100;

      if (Math.abs(balanceAmount - expectedBalanceAmount) < 0.01) {
        return true;
      }

      return this.createError({
        path: 'payment_metadata.payment_flow_v2.manual_balance_amount',
        message: 'Manual balance amount must equal the sell price minus the manual deposit amount.',
      });
    })
    .test('v2-manual-date-ordering', function (value) {
      if (value?.payment_metadata?.payment_flow_v2?.type !== 'manual') {
        return true;
      }

      const depositDueDate = value.payment_metadata.payment_flow_v2.manual_deposit_due_date;
      const balanceDueDate = value.payment_metadata.payment_flow_v2.manual_balance_due_date;

      if (!depositDueDate || !balanceDueDate) {
        return true;
      }

      const normalizedDepositDueDate = new Date(depositDueDate);
      const normalizedBalanceDueDate = new Date(balanceDueDate);

      normalizedDepositDueDate.setHours(0, 0, 0, 0);
      normalizedBalanceDueDate.setHours(0, 0, 0, 0);

      if (normalizedBalanceDueDate.getTime() >= normalizedDepositDueDate.getTime()) {
        return true;
      }

      return this.createError({
        path: 'payment_metadata.payment_flow_v2.manual_balance_due_date',
        message: 'Manual balance due date must be on or after the manual deposit due date.',
      });
    });

const paymentMetadataGHStoreSchema = yup.object({
  margin: yup.number().typeError('Please enter a valid number for margin.').default(0),
  le_service_fee: yup.number().typeError('Please enter a valid number for service fee.').default(0),
  payment_type: yup.string().oneOf(['full_payment']).default('full_payment'),
  final_amount_customer: yup
    .number()
    .typeError('Please enter a valid number for final amount.')
    .required('Please enter the final amount for the customer.')
    .min(1, 'Final amount for the customer must be at least 1.'),
  block_customer_payment_notifications: yup.boolean().default(false),
  sales_attribution: yup.string().required('Sales Attribution is required'),
  sales_agent_name: yup.string().required('Please select the Wholesale Agent.'),
  additional_margin: yup
    .number()
    .transform((value, original) => (original === '' || isNaN(original) ? null : value))
    .nullable(),
  final_amount_customer_display: yup
    .number()
    .typeError('Please enter a valid number for final amount to display.')
    .optional()
    .nullable(),
});

const paymentMetadataLeLiveSchema = yup
  .object({
    margin: yup.number().typeError('Please enter a valid number for margin.').default(0),
    le_service_fee: yup.number().typeError('Please enter a valid number for service fee.').default(0),
    final_amount_vendor: yup
      .number()
      .typeError('Please enter a valid number for vendor final amount.')
      .default(0)
      .min(0, 'Vendor final amount cannot be negative.'),
    internal_discount_tracking: internalDiscountTrackingSchema,
    final_amount_customer: yup
      .number()
      .typeError('Please enter a valid number for final amount.')
      .required('Please enter the final amount for the customer.')
      .min(1, 'Final amount for the customer must be at least 1.'),
    block_customer_payment_notifications: yup.boolean().default(false),
    sales_attribution: yup.string().required('Sales Attribution is required'),
    sales_agent_name: yup.string().required('Please select the Wholesale Agent.'),
    additional_margin: yup
      .number()
      .transform((value, original) => (original === '' || isNaN(original) ? null : value))
      .nullable(),
  })
  .concat(paymentMetadataSchema.pick(['payment_flow_v2']));

// Factory function to create custom offer flight schema with tenant-specific validation
// flight_legs is required for lebusinesstraveller tenant, optional for others
// purchaseType selects the appropriate traveller schema (fare_and_taxes vs airline_fee + le_fee)
export const createCustomOfferFlightSchema = (tenant?: string, purchaseType: 'sales' | 'post-purchase' = 'sales') => {
  const activeTravellerSchema =
    purchaseType === 'post-purchase' ? travellerFlightPostPurchaseSchema : travellerFlightSchema;

  return appendCustomOfferFlightPaymentTests(
    yup.object({
      sold_at: yup.date().required('Please enter the sold date.'),
      price: yup
        .number()
        .typeError('Please enter a valid number for sell price.')
        .default(0)
        .required('Please enter the sell price.')
        .moreThan(0, 'Sell price must be greater than 0.')
        .max(9999999999, 'Sell price cannot be larger than 9,999,999,999.'),
      price_currency: yup
        .string()
        .default('AUD')
        .required('Please select the customer currency.')
        .oneOf(currencyCodes, 'Please select a valid customer currency.'),
      cost_price: yup
        .number()
        .typeError('Please enter a valid number for cost price.')
        .default(0)
        .required('Please enter the cost price.')
        .when('post_purchase_type', ([postPurchaseType], schema) =>
          purchaseType === PURCHASE_TYPE_POST_PURCHASE && postPurchaseType === POST_PURCHASE_TYPE_NAME_CORRECTION
            ? schema.min(0, 'Cost price cannot be negative.')
            : schema.moreThan(0, 'Cost price must be greater than 0.'),
        )
        .max(9999999999, 'Cost price cannot be larger than 9,999,999,999.'),
      payment_metadata: paymentMetadataFlightSchema.required(),
      item: yup.object(createItemFlightSchema(tenant, purchaseType)).required('Item details are required.'),
      travellers: yup
        .array(activeTravellerSchema)
        .min(1, 'Please add at least one traveller.')
        .max(10, 'Maximum of 10 travellers allowed.'),
      post_purchase_type:
        purchaseType === 'post-purchase'
          ? yup.string().required('Please select a post-purchase type.')
          : yup.string().optional().nullable(),
      reason:
        purchaseType === 'post-purchase'
          ? yup.string().optional().nullable()
          : yup.string().required('Please select a reason for this custom offer.'),
      reasonOther: yup.string().when('reason', {
        is: (reason: string | undefined) => isReasonOtherTextRequired(reason),
        then: () => yup.string().required('Please specify the reason for this custom offer.'),
      }),
      fk_package_custom_offer_id: yup.string().nullable().optional(),
      hiddenFromCustomer: yup.boolean().default(false),
      pdf: yup
        .object({
          filename: yup.string().optional(),
          content: yup.string().optional(),
          mimeType: yup.string().oneOf(['application/pdf'], 'Only PDF files are allowed.').optional(),
        })
        .optional()
        .nullable(),
    }),
  );
};

// Default schema for backward compatibility (flight_legs is optional)
export const customOfferFlightSchema = createCustomOfferFlightSchema();

export const customOfferGHStoreSchema = yup.object({
  due_date: yup
    .date()
    .required('Please enter the customer payment due date.')
    .min(today, 'Customer payment due date must be in the future.'),
  sold_at: yup.date().required('Please enter the sold date.'),
  price: yup
    .number()
    .typeError('Please enter a valid number for sell price.')
    .default(0)
    .when('item', (item: any, schema: any) => {
      const type = Array.isArray(item) ? item[0]?.type : item?.type;
      return type === 'custom-offer-package'
        ? schema
            .required('Please enter the sell price.')
            .max(0, 'Sell price must be 0 for a package.')
            .min(0, 'Sell price must be 0 for a package.')
        : schema
            .required('Please enter the sell price.')
            .moreThan(0, 'Sell price must be greater than 0.')
            .max(9999999999, 'Sell price cannot be larger than 9,999,999,999.');
    }),
  price_currency: yup
    .string()
    .default('AUD')
    .required('Please select the customer currency.')
    .oneOf(currencyCodes, 'Please select a valid customer currency.'),
  cost_price: yup
    .number()
    .typeError('Please enter a valid number for cost price.')
    .default(0)
    .when('item', (item: any, schema: any) => {
      const type = Array.isArray(item) ? item[0]?.type : item?.type;
      return type === 'custom-offer-package'
        ? schema
            .required('Please enter the cost price.')
            .max(0, 'Cost price must be 0 for a package.')
            .min(0, 'Cost price must be 0 for a package.')
        : schema
            .required('Please enter the cost price.')
            .moreThan(0, 'Cost price must be greater than 0.')
            .max(9999999999, 'Cost price cannot be larger than 9,999,999,999.');
    }),
  cost_price_currency: yup.string().default('AUD').required('Please select the vendor currency.'),
  payment_terms: yup.string().optional().nullable(),
  payment_metadata: yup
    .object()
    .when('item', (item: any, schema: any) => {
      const type = Array.isArray(item) ? item[0]?.type : item?.type;
      if (type === 'custom-offer-package') {
        return paymentMetadataGHStoreSchema.concat(
          yup.object({
            final_amount_customer: yup
              .number()
              .typeError('Please enter a valid number for final amount.')
              .required('Please enter the final amount for the customer.')
              .max(0, 'Final amount for the customer must be 0 for a package.')
              .min(0, 'Final amount for the customer must be 0 for a package.'),
            final_amount_customer_display: yup
              .number()
              .typeError('Please enter a valid number for final amount to display.')
              .required('Please enter the final amount to display.')
              .moreThan(0, 'Final amount to display must be greater than 0.'),
          }),
        );
      }
      return paymentMetadataGHStoreSchema;
    })
    .required(),
  item: yup.object(itemSchema).required('Item details are required.'),
  travellers: yup
    .array(travellerSchema)
    .min(1, 'Please add at least one traveller.')
    .max(10, 'Maximum of 10 travellers allowed.'),
  reason: yup.string().required('Please select a reason for this custom offer.'),
  reasonOther: yup.string().when('reason', {
    is: (reason: string | undefined) => isReasonOtherTextRequired(reason),
    then: () => yup.string().required('Please specify the reason for this custom offer.'),
  }),
  supplier: yup.string().required('Please select a supplier.'),
  notes: yup.string().optional().nullable(),
  images: yup.array().optional().nullable(),
  created_from_item_id: yup.string().optional().nullable(),
  fk_package_custom_offer_id: yup.string().nullable().optional(),
  pdf_filename: yup.string().optional().nullable(),
  pdf_s3_key: yup.string().optional().nullable(),
  pdf_download_url: yup.string().optional().nullable(),
  hiddenFromCustomer: yup.boolean().default(false),
  pdf: yup
    .object({
      filename: yup.string().optional(),
      content: yup.string().optional(),
      mimeType: yup.string().oneOf(['application/pdf'], 'Only PDF files are allowed.').optional(),
    })
    .optional()
    .nullable(),
});

export const customOfferBaseSchema = yup
  .object({
    due_date: yup
      .date()
      .required('Please enter the customer payment due date.')
      .min(today, 'Customer payment due date must be same day or in the future.'),
    sold_at: yup.date().required('Please enter the sold date.'),
    price: yup
      .number()
      .typeError('Please enter a valid number for sell price.')
      .default(0)
      .required('Please enter the sell price.')
      .moreThan(0, 'Sell price must be greater than 0.')
      .max(9999999999, 'Sell price cannot be larger than 9,999,999,999.'),
    price_currency: yup
      .string()
      .default('AUD')
      .required('Please select the customer currency.')
      .oneOf(currencyCodes, 'Please select a valid customer currency.'),
    cost_price: yup
      .number()
      .typeError('Please enter a valid number for cost price.')
      .default(0)
      .required('Please enter the cost price.')
      .moreThan(0, 'Cost price must be greater than 0.')
      .max(9999999999, 'Cost price cannot be larger than 9,999,999,999.'),
    // cost price is a select field, user can only select values from the list.
    cost_price_currency: yup.string().default('AUD').required('Please select the vendor currency.'),
    payment_terms: yup.string().optional().nullable(),
    payment_metadata: paymentMetadataSchema.required(),
    item: yup.object(itemSchema).required('Item details are required.'),
    travellers: yup
      .array(travellerSchema)
      .min(1, 'Please add at least one traveller.')
      .max(10, 'Maximum of 10 travellers allowed.'),
    purchase_type: yup.string().oneOf([PURCHASE_TYPE_SALES, PURCHASE_TYPE_POST_PURCHASE]).default(PURCHASE_TYPE_SALES),
    post_purchase_type: yup.string().when(['item.type', 'purchase_type'], ([itemType, purchaseType], schema) =>
      isCruiseItemType(itemType) && purchaseType === PURCHASE_TYPE_POST_PURCHASE
        ? schema
            .transform((value) => (value === '' ? undefined : value))
            .oneOf(CRUISE_POST_PURCHASE_TYPE_VALUES, 'Please select a valid post-purchase type.')
            .required('Please select a post-purchase type.')
        : schema.optional().nullable(),
    ),
    reason: yup.string().required('Please select a reason for this custom offer.'),
    reasonOther: yup.string().when('reason', {
      is: (reason: string | undefined) => isReasonOtherTextRequired(reason),
      then: () => yup.string().required('Please specify the reason for this custom offer.'),
    }),
    supplier: yup.string().required('Please select a supplier.'),
    hiddenFromCustomer: yup.boolean().default(false),
    sentToAtiTicketing: yup.boolean().default(false),
  })
  .test('v2-payment-schedule-deposit-amount', function (value) {
    if (value?.payment_metadata?.payment_flow_v2?.type !== 'payment_schedule') {
      return true;
    }

    const depositAmount = Number(value.payment_metadata.payment_flow_v2.deposit_amount ?? 0);
    const price = Number(value.price ?? 0);

    if (depositAmount <= price) {
      return true;
    }

    return this.createError({
      path: 'payment_metadata.payment_flow_v2.deposit_amount',
      message: 'Deposit amount cannot exceed the sell price.',
    });
  })
  .test('v2-payment-schedule-balance-amount', function (value) {
    if (value?.payment_metadata?.payment_flow_v2?.type !== 'payment_schedule') {
      return true;
    }

    const depositAmount = Number(value.payment_metadata.payment_flow_v2.deposit_amount ?? 0);
    const balanceAmount = Number(value.payment_metadata.payment_flow_v2.balance_amount ?? 0);
    const price = Number(value.price ?? 0);
    const expectedBalanceAmount = Math.round((price - depositAmount) * 100) / 100;

    if (Math.abs(balanceAmount - expectedBalanceAmount) < 0.01) {
      return true;
    }

    return this.createError({
      path: 'payment_metadata.payment_flow_v2.balance_amount',
      message: 'Balance amount must equal the sell price minus the deposit amount.',
    });
  })
  .test('v2-payment-schedule-date-ordering', function (value) {
    if (value?.payment_metadata?.payment_flow_v2?.type !== 'payment_schedule') {
      return true;
    }

    const paymentDeadline = value.payment_metadata.payment_flow_v2.payment_deadline;
    const balanceDueDate = value.payment_metadata.payment_flow_v2.balance_due_date;

    if (!paymentDeadline || !balanceDueDate) {
      return true;
    }

    const normalizedPaymentDeadline = new Date(paymentDeadline);
    const normalizedBalanceDueDate = new Date(balanceDueDate);

    normalizedPaymentDeadline.setHours(0, 0, 0, 0);
    normalizedBalanceDueDate.setHours(0, 0, 0, 0);

    if (normalizedBalanceDueDate.getTime() >= normalizedPaymentDeadline.getTime()) {
      return true;
    }

    return this.createError({
      path: 'payment_metadata.payment_flow_v2.balance_due_date',
      message: 'Final payment due date must be on or after the payment deadline.',
    });
  })
  .test('v2-manual-deposit-amount', function (value) {
    if (value?.payment_metadata?.payment_flow_v2?.type !== 'manual') {
      return true;
    }

    const depositAmount = Number(value.payment_metadata.payment_flow_v2.manual_deposit_amount ?? 0);
    const price = Number(value.price ?? 0);

    if (depositAmount <= price) {
      return true;
    }

    return this.createError({
      path: 'payment_metadata.payment_flow_v2.manual_deposit_amount',
      message: 'Manual deposit amount cannot exceed the sell price.',
    });
  })
  .test('v2-manual-balance-amount', function (value) {
    if (value?.payment_metadata?.payment_flow_v2?.type !== 'manual') {
      return true;
    }

    const depositAmount = Number(value.payment_metadata.payment_flow_v2.manual_deposit_amount ?? 0);
    const balanceAmount = Number(value.payment_metadata.payment_flow_v2.manual_balance_amount ?? 0);
    const price = Number(value.price ?? 0);
    const expectedBalanceAmount = Math.round((price - depositAmount) * 100) / 100;

    if (Math.abs(balanceAmount - expectedBalanceAmount) < 0.01) {
      return true;
    }

    return this.createError({
      path: 'payment_metadata.payment_flow_v2.manual_balance_amount',
      message: 'Manual balance amount must equal the sell price minus the manual deposit amount.',
    });
  })
  .test('v2-manual-date-ordering', function (value) {
    if (value?.payment_metadata?.payment_flow_v2?.type !== 'manual') {
      return true;
    }

    const depositDueDate = value.payment_metadata.payment_flow_v2.manual_deposit_due_date;
    const balanceDueDate = value.payment_metadata.payment_flow_v2.manual_balance_due_date;

    if (!depositDueDate || !balanceDueDate) {
      return true;
    }

    const normalizedDepositDueDate = new Date(depositDueDate);
    const normalizedBalanceDueDate = new Date(balanceDueDate);

    normalizedDepositDueDate.setHours(0, 0, 0, 0);
    normalizedBalanceDueDate.setHours(0, 0, 0, 0);

    if (normalizedBalanceDueDate.getTime() >= normalizedDepositDueDate.getTime()) {
      return true;
    }

    return this.createError({
      path: 'payment_metadata.payment_flow_v2.manual_balance_due_date',
      message: 'Manual balance due date must be on or after the manual deposit due date.',
    });
  });

export const customOfferLeLiveSchema = yup.object({
  due_date: yup
    .date()
    .required('Please enter the customer payment due date.')
    .min(today, 'Customer payment due date must be in the future.'),
  sold_at: yup.date().required('Please enter the sold date.'),
  price: yup
    .number()
    .typeError('Please enter a valid number for sell price.')
    .default(0)
    .required('Please enter the sell price.')
    .moreThan(0, 'Sell price must be greater than 0.')
    .max(9999999999, 'Sell price cannot be larger than 9,999,999,999.'),
  price_currency: yup
    .string()
    .default('AUD')
    .required('Please select the customer currency.')
    .oneOf(currencyCodes, 'Please select a valid customer currency.'),
  cost_price: yup
    .number()
    .typeError('Please enter a valid number for cost price.')
    .default(0)
    .required('Please enter the cost price.')
    .min(0, 'Cost price cannot be negative.')
    .max(9999999999, 'Cost price cannot be larger than 9,999,999,999.'),
  cost_price_currency: yup.string().default('AUD').required('Please select the vendor currency.'),
  payment_terms: yup.string().optional().nullable(),
  payment_metadata: paymentMetadataLeLiveSchema.required(),
  item: leLiveItemSchema.required('Item details are required.'),
  travellers: yup
    .array(leLiveTravellerSchema)
    .min(1, 'Please add at least one traveller.')
    .max(10, 'Maximum of 10 travellers allowed.'),
  reason: yup.string().optional().nullable(),
  reasonOther: yup.string().optional().nullable(),
  supplier: yup.string().optional().nullable(),
  notes: yup.string().optional().nullable(),
  images: yup.array().optional().nullable(),
  created_from_item_id: yup.string().optional().nullable(),
  pdf_filename: yup.string().optional().nullable(),
  pdf_s3_key: yup.string().optional().nullable(),
  pdf_download_url: yup.string().optional().nullable(),
  pdf: yup
    .object({
      filename: yup.string().optional(),
      content: yup.string().optional(),
      mimeType: yup.string().oneOf(['application/pdf'], 'Only PDF files are allowed.').optional(),
    })
    .optional()
    .nullable(),
});

export const customOfferPackageSchema = yup.object({
  due_date: yup
    .date()
    .required('Please enter the customer payment due date.')
    .min(today, 'Customer payment due date must be same day or in the future.'),
  sold_at: yup.date().required('Please enter the sold date.'),
  price: yup
    .number()
    .typeError('Please enter a valid number for sell price.')
    .default(0)
    .required('Please enter the sell price.')
    .max(0, 'Sell price must be 0 for a package.')
    .min(0, 'Sell price must be 0 for a package.'),
  price_currency: yup
    .string()
    .default('AUD')
    .required('Please select the customer currency.')
    .oneOf(currencyCodes, 'Please select a valid customer currency.'),
  cost_price: yup
    .number()
    .typeError('Please enter a valid number for cost price.')
    .default(0)
    .required('Please enter the cost price.')
    .max(0, 'Cost price must be 0 for a package.')
    .min(0, 'Cost price must be 0 for a package.'),
  cost_price_currency: yup.string().default('AUD').required('Please select the vendor currency.'),
  payment_terms: yup.string().optional().nullable(),
  payment_metadata: paymentMetadataSchema.concat(
    yup.object({
      final_amount_vendor: yup
        .number()
        .typeError('Please enter a valid number for final amount.')
        .required('Please enter the final amount for the vendor.')
        .max(0, 'Final amount for the vendor must be 0 for a package.')
        .min(0, 'Final amount for the vendor must be 0 for a package.'),
      final_amount_customer: yup
        .number()
        .typeError('Please enter a valid number for final amount.')
        .required('Please enter the final amount for the customer.')
        .max(0, 'Final amount for the customer must be 0 for a package.')
        .min(0, 'Final amount for the customer must be 0 for a package.'),
      payment_type: yup
        .string()
        .oneOf(['full_payment'], 'Payment type must be full_payment for a package.')
        .required('Please select the customer payment type.'),
      final_amount_customer_display: yup
        .number()
        .typeError('Please enter a valid number for final amount to display.')
        .required('Please enter the final amount to display.')
        .moreThan(0, 'Final amount to display must be greater than 0.'),
    }),
  ),
  item: yup.object(itemSchema).required('Item details are required.'),
  travellers: yup
    .array(travellerSchema)
    .min(1, 'Please add at least one traveller.')
    .max(10, 'Maximum of 10 travellers allowed.'),
  reason: yup.string().required('Please select a reason for this custom offer.'),
  reasonOther: yup.string().when('reason', {
    is: (reason: string | undefined) => isReasonOtherTextRequired(reason),
    then: () => yup.string().required('Please specify the reason for this custom offer.'),
  }),
  supplier: yup.string().required('Please select a supplier.'),
  fk_package_custom_offer_id: yup.string().nullable().optional(),
  hiddenFromCustomer: yup.boolean().default(false),
});

const cruisePostPurchaseItemSchema = yup.object(itemSchema).shape({
  cruise_line: yup.string().optional(),
  start_date: yup.date().required('Please enter the departure date.'),
  end_date: yup.date().optional().nullable(),
  ship_name: yup.string().optional(),
  ship_cabin: yup.string().optional(),
  ship_rate: yup.string().optional(),
  // City and country are hidden on cruise post-purchase, so they stay optional here.
  city: yup.string().optional().nullable(),
  country: yup.string().optional().nullable(),
});

const cruisePostPurchaseCustomOfferSchema = customOfferBaseSchema.shape({
  item: cruisePostPurchaseItemSchema.required('Item details are required.'),
  reason: yup.string().optional().nullable(),
  reasonOther: yup.string().optional().nullable(),
});

const cruiseRefareCustomOfferSchema = cruisePostPurchaseCustomOfferSchema
  .shape({
    payment_metadata: paymentMetadataSchema.shape({
      refare_new_fare: refareNewFareSchema.required('Please enter the new fare.'),
      refare_new_tax_and_surcharges: refareNewTaxAndSurchargesSchema.required(
        'Please enter the new tax and surcharges.',
      ),
      refare_deposit_fee_already_paid: refareDepositFeeAlreadyPaidSchema.required(
        'Please enter the deposit or fee already paid.',
      ),
    }),
  })
  .test('cruise-refare-balance', function (value) {
    const paymentMetadata = value?.payment_metadata;
    const { total } = calculateCruisePostPurchaseTotals({
      cruiselineAdditionalCharges: Number(paymentMetadata?.cruiseline_additional_charges ?? 0),
      cruiselineFee: Number(paymentMetadata?.cruiseline_fee ?? 0),
      cruiseLeFee: Number(paymentMetadata?.le_fee ?? 0),
      refareDepositFeeAlreadyPaid: Number(paymentMetadata?.refare_deposit_fee_already_paid ?? 0),
      refareNewFare: Number(paymentMetadata?.refare_new_fare ?? 0),
      refareNewTaxAndSurcharges: Number(paymentMetadata?.refare_new_tax_and_surcharges ?? 0),
    });

    if (!Number.isFinite(total)) {
      return true;
    }

    if (total >= 0) {
      return true;
    }

    return this.createError({
      path: 'payment_metadata.refare_deposit_fee_already_paid',
      message: 'The balance to pay cannot be negative. Process any refund separately.',
    });
  });

const isCruisePostPurchaseCustomOffer = (value: unknown) => {
  const customOffer = value as { item?: { type?: string }; purchase_type?: string };
  return isCruiseItemType(customOffer?.item?.type) && customOffer?.purchase_type === PURCHASE_TYPE_POST_PURCHASE;
};

const isCruiseRefareCustomOffer = (value: unknown) => {
  const customOffer = value as { post_purchase_type?: string };
  return isCruisePostPurchaseCustomOffer(value) && customOffer?.post_purchase_type === CRUISE_POST_PURCHASE_TYPE_REFARE;
};

export const customOfferSchema = yup.lazy((value: unknown) => {
  const itemType = (value as { item?: { type?: string } })?.item?.type;

  if (itemType === 'custom-offer-package') {
    return customOfferPackageSchema;
  }

  if (itemType === 'le_live') {
    return customOfferLeLiveSchema;
  }

  if (isCruisePostPurchaseCustomOffer(value)) {
    return isCruiseRefareCustomOffer(value) ? cruiseRefareCustomOfferSchema : cruisePostPurchaseCustomOfferSchema;
  }

  return customOfferBaseSchema;
});

const editableCustomOfferBaseSchema = customOfferBaseSchema
  .pick(['payment_terms', 'item', 'travellers', 'purchase_type', 'post_purchase_type', 'sentToAtiTicketing'])
  .concat(
    yup.object({
      payment_metadata: paymentMetadataSchema.pick([
        'customer_deposit_payment_made',
        'vendor_deposit_payment_made',
        'vendor_was_paid',
      ]),
      fk_package_custom_offer_id: yup.string().nullable().optional(),
    }),
  );

export const editableCustomOfferSchema = yup.lazy((value: unknown) => {
  if (isCruisePostPurchaseCustomOffer(value)) {
    return editableCustomOfferBaseSchema.shape({
      item: cruisePostPurchaseItemSchema.required('Item details are required.'),
    });
  }

  return editableCustomOfferBaseSchema;
});

export const editableCustomOfferLeLiveSchema = yup.object({
  payment_terms: yup.string().optional().nullable(),
  item: leLiveItemSchema.required('Item details are required.'),
  travellers: yup
    .array(leLiveTravellerSchema)
    .min(1, 'Please add at least one traveller.')
    .max(10, 'Maximum of 10 travellers allowed.'),
  payment_metadata: paymentMetadataSchema.pick([
    'customer_deposit_payment_made',
    'vendor_deposit_payment_made',
    'vendor_was_paid',
    'internal_discount_tracking',
    'payment_flow_v2',
  ]),
});

const createEditableItemFlightBaseSchema = (purchaseType: 'sales' | 'post-purchase' = 'sales') => ({
  type: yup.string().required('Please select an item type.').oneOf(['flight'], 'Please select a valid item type.'),
  provider: yup
    .string()
    .required('Please select the provider.')
    .oneOf(flightProviderValues, 'Please select a valid provider.'),
  vendor_reference_number: yup
    .string()
    .trim()
    .required('Please enter the PNR.')
    .matches(FLIGHT_PNR_REGEX, FLIGHT_PNR_INVALID_MESSAGE),
  name: yup
    .string()
    .required('Please enter the item name.')
    .max(300, 'Item name cannot be longer than 300 characters.'),
  service_information:
    purchaseType === 'sales'
      ? yup.string().required('Please enter the terms and conditions.')
      : yup.string().optional().nullable(),
  salesforce_case_number: yup.string().required('Please enter the Zendesk ticket number.'),
  flight_type: yup.string().oneOf(['one-way', 'return', 'multi-city']).required(),
  // Start date without future validation for edit mode
  start_date: yup.date().when('flight_type', {
    is: 'one-way',
    then: (schema) => schema.required('Please enter the start date.'),
    otherwise: (schema) =>
      schema
        .required('Please enter the start date.')
        .test('is-before-or-equal-to-end', 'Start date must be on or before the end date.', function (value) {
          const endDate = this.parent.end_date;
          if (!value || !endDate) {
            return true;
          }
          const start = new Date(value);
          const end = new Date(endDate);
          start.setHours(0, 0, 0, 0);
          end.setHours(0, 0, 0, 0);
          return start.getTime() <= end.getTime();
        }),
  }),
  // End date without future validation for edit mode
  end_date: yup.date().when('flight_type', {
    is: (val: string | undefined) => isFlightEndDateRequired(val),
    then: (schema) =>
      schema
        .required('Please enter the end date.')
        .test('is-after-or-equal-to-start', 'End date must be on or after the start date.', function (value) {
          const startDate = this.parent.start_date;
          if (!value || !startDate) {
            return true;
          }
          const start = new Date(startDate);
          const end = new Date(value);
          start.setHours(0, 0, 0, 0);
          end.setHours(0, 0, 0, 0);
          return end.getTime() >= start.getTime();
        }),
    otherwise: (schema) => schema.optional(),
  }),
});

const createEditableItemFlightSchema = (tenant?: string, purchaseType: 'sales' | 'post-purchase' = 'sales') => ({
  ...createEditableItemFlightBaseSchema(purchaseType),
  travel_scope: createTravelScopeSchema(purchaseType),
  flight_legs: createFlightLegsSchema(tenant),
});

// Factory function to create editable custom offer flight schema with tenant-specific validation
// flight_legs is required for lebusinesstraveller tenant, optional for others
// purchaseType selects the appropriate traveller schema
export const createEditableCustomOfferFlightSchema = (
  tenant?: string,
  purchaseType: 'sales' | 'post-purchase' = 'sales',
) => {
  const activeTravellerSchema =
    purchaseType === 'post-purchase' ? travellerFlightPostPurchaseSchema : travellerFlightSchema;

  return yup.object({
    due_date: yup.date().optional(),
    sold_at: yup.date().optional(),
    price: yup.number().optional(),
    price_currency: yup.string().optional(),
    cost_price: yup.number().optional(),
    fk_package_custom_offer_id: yup.string().nullable().optional(),
    item: yup.object(createEditableItemFlightSchema(tenant, purchaseType)).required('Item details are required.'),
    travellers: yup
      .array(activeTravellerSchema)
      .min(1, 'Please add at least one traveller.')
      .max(10, 'Maximum of 10 travellers allowed.'),
    payment_metadata: paymentMetadataFlightSchema.pick(['margin', 'le_service_fee']),
    pdf: yup
      .object({
        filename: yup.string().optional(),
        content: yup.string().optional(),
        mimeType: yup.string().oneOf(['application/pdf'], 'Only PDF files are allowed.').optional(),
      })
      .optional()
      .nullable(),
  });
};

// Default schema for backward compatibility (flight_legs is optional)
export const editableCustomOfferFlightSchema = createEditableCustomOfferFlightSchema();
